/* eslint-disable max-statements */
/* eslint-disable react/jsx-max-depth */
import { Button, Form, FormControl, InputGroup, Nav } from "react-bootstrap";
import { DesktopComputerIcon, FilterIcon, LocationMarkerIcon, SearchIcon, XIcon } from "@heroicons/react/outline";
import { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { debounce } from "lodash";
import { useSearchParams } from "react-router-dom";

import { Default, Desktop, LargeDesktop, Mobile, Tablet } from "../../components/Devices";
import CalendarMonthNavigator from "./components/CalendarMonthCycle";
import PageHeader from "../../components/PageHeader";
import memoize from "fast-memoize";
import { useClassFilters } from "./components/ClassFilterProvider";

const CalendarEventFilters = () => {
	const [searchParams] = useSearchParams();
	const {
		setFacetValues,
		setFilters,
		showFilters,
		toggleFilters,
		isFiltered,
		setMySearchParams
	} = useClassFilters();

	const [courseType, setCourseType] = useState(null);
	// eslint-disable-next-line no-confusing-arrow
	const handleSetType = useCallback(memoize((type) => () => courseType === type ? setCourseType(null) : setCourseType(type)));
	useEffect(() => {
		setFilters({ courseType });
	}, [courseType]);

	const handleChange = useCallback((event) => {
		setFilters({
			[event.target.name]: event.target.value
		});

		setMySearchParams({
			search: event.target.value
		});
	}, []);
	const debouncedChange = useCallback(debounce(handleChange, 300), []);
	const handleSubmit = useCallback((evt) => {
		evt.preventDefault();
		evt.target.blur();
		if ("virtualKeyboard" in navigator) {
			// The VirtualKeyboard API is supported!
			navigator.virtualKeyboard.hide();
		}
	}, []);
	const handleResetFilters = useCallback((evt) => {
		evt.preventDefault();
		setFacetValues({});
	}, []);

	useEffect(() => {
		const search = searchParams.get("search");
		if (search) {
			setFilters({ search });
		}
	}, []);

	return (
		<PageHeader
			className="position-sticky"
			fluid
			id="calendar-search-bar"
			style={{ zIndex: "10" }}
			title="Event Registration"
			titleClassName="me-auto d-lg-block d-none"
		>
			<Default>
				<CalendarMonthNavigator />
				<Nav className="py-1">
					<Nav.Item className="me-2 justify-content-center py-3 align-items-center d-flex">
						<Button
							active={!courseType}
							className="pill text-uppercase"
							onClick={handleSetType("")}
							size="sm"
							variant="outline-secondary"
						>
							All
						</Button>
					</Nav.Item>
					<Nav.Item className="me-2 justify-content-center py-1 align-items-center d-flex">
						<Button
							active={courseType === "online"}
							className="pill text-uppercase"
							onClick={handleSetType("online")}
							size="sm"
							variant="outline-secondary"
						>
							<DesktopComputerIcon />
							<LargeDesktop>
								Online
							</LargeDesktop>
						</Button>
					</Nav.Item>

					<Nav.Item className="me-2 justify-content-center py-1 align-items-center d-flex">
						<Button
							active={courseType === "in-person"}
							className="pill text-uppercase"
							onClick={handleSetType("in-person")}
							size="sm"
							variant="outline-secondary"
						>
							<LocationMarkerIcon />
							<LargeDesktop>
								In Person
							</LargeDesktop>
						</Button>
					</Nav.Item>
					<Nav.Item className="me-2 justify-content-center align-items-center d-flex">
						<Form onSubmit={handleSubmit}>
							<InputGroup className="w-100 py-3 py-md-0">
								<LargeDesktop>
									<InputGroup.Text className="bg-white text-neutral6 d-lg-flex search-icon">
										<SearchIcon className="i" />
									</InputGroup.Text>
								</LargeDesktop>
								<FormControl
									aria-label="Search"
									defaultValue={searchParams.get("search")}
									name="search"
									onChange={debouncedChange}
									placeholder="Search"
									type="search"
								/>
								<Button
									active={showFilters}
									className={classNames({
										"text-neutral6": !showFilters,
										"text-green": showFilters,
										"has-filters": isFiltered
									})}
									onClick={toggleFilters}
									variant="link"
								>
									<FilterIcon />
								</Button>
								<Button
									className="text-neutral6 color-neutral6"
									onClick={handleResetFilters}
									variant="link"
								>
									<Desktop>
										Reset
									</Desktop>
									<Tablet>
										<XIcon className="i text-neutral6" />
									</Tablet>
								</Button>
							</InputGroup>
						</Form>
					</Nav.Item>
				</Nav>
			</Default>
			<Mobile>
				<Form className="w-100 py-3" onSubmit={handleSubmit}>
					<InputGroup className="w-100">
						<FormControl
							aria-label="Search"
							defaultValue={searchParams.get("search")}
							name="search"
							onChange={debouncedChange}
							placeholder="Search"
							type="search"
						/>
						<Button
							className={classNames("border-neutral4", {
								"has-filters": isFiltered
							})}
							onClick={toggleFilters}
							variant="outline-secondary"
						>
							<FilterIcon />
						</Button>
					</InputGroup>
				</Form>
			</Mobile>
		</PageHeader>
	);
};

export default CalendarEventFilters;
